import type { Device } from "@/api/generated/model"
import type { RangeValue } from "@/components/kit/RangePicker"
import { CUSTOMER_PERMISSIONS, isStaff } from "@/auth/staff"

export { delta, type Delta } from "@/lib/delta"

export type OverviewRange = RangeValue

export { rangeFromUrl, rangeParams as toOverviewParams } from "@/components/kit/RangePicker"

/** The aligned window before `r`: the same length, ending where `r` starts (backend `previous`). */
export function previousRange(r: { from: string; to: string }): { from: string; to: string } {
  const from = Date.parse(r.from)
  const length = Date.parse(r.to) - from
  return { from: new Date(from - length).toISOString(), to: r.from }
}

/** The busiest phones, by everything they sent and received. */
export function topDevices(items: readonly Device[], n: number): Device[] {
  const traffic = (d: Device) => d.sentSMSCount + d.receivedSMSCount
  return [...items].sort((a, b) => traffic(b) - traffic(a)).slice(0, n)
}

export type StepId = "device" | "key" | "message"
export interface Checklist {
  steps: { id: StepId; done: boolean }[]
  visible: boolean
}

/**
 * Get started (FR-DSH-OVW-06), from counts (DW-04). A count is `undefined`
 * when the caller may not read it: that step is left out rather than shown
 * as never done. Shown until every step it shows is done.
 */
export function checklist(counts: {
  devices: number | undefined
  keys: number | undefined
  messages: number | undefined
}): Checklist {
  const all: [StepId, number | undefined][] = [
    ["device", counts.devices],
    ["key", counts.keys],
    ["message", counts.messages],
  ]
  const steps = all
    .filter((s): s is [StepId, number] => s[1] !== undefined)
    .map(([id, n]) => ({ id, done: n > 0 }))
  return { steps, visible: steps.some((s) => !s.done) }
}

/**
 * LLD §9.3 staff landing: staff holding no customer permission beyond
 * `account.*` would see a page of absent regions; they start at `/admin`.
 */
export function landsOnAdmin(permissions: readonly string[]): boolean {
  if (!isStaff(permissions)) return false
  return !permissions.some((p) => CUSTOMER_PERMISSIONS.has(p) && !p.startsWith("account."))
}
